/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: PagingGroupsFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Megaphone, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { MultiSelectModal } from '../../components/ui/MultiSelectModal';
import { Phone } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { deletePagingGroupById, navigateToPagingGroupDuplicate } from './pagingGroupsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';

import { BasicTab } from './tabs/BasicTab';
import { AdvancedTab } from './tabs/AdvancedTab';

interface MulticastRoute {
  address: string;
  port: number;
  mode: 'speak' | 'listen';
  interface: string;
  ttl: number;
  codec: string;
  enabled: boolean;
}

const defaultMulticastRoute: MulticastRoute = {
  address: '239.255.10.10', port: 5000, mode: 'speak', interface: 'eth0', ttl: 1, codec: 'PCMU', enabled: true
};

export default function PagingGroupsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const lastSaveTimestamp = useRef<number>(0);
  const location = useLocation();
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const showErrorRef = useRef(showError);

  useEffect(() => { showErrorRef.current = showError; }, [showError]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const generateContext = (name: string): string =>
    name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').substring(0, 50);

  const defaultSettings = {
    codec: 'PCMU', volume: 5, timeout: 15, recordEnabled: false, skipBusy: false,
    multicastMode: 'speak', conferenceProfile: 'page', holdMusic: 'local_stream://default'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as Record<string, any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultFormData = { code: '', name: '', context: '', mode: 'default', enabled: true, settings: defaultSettings as Record<string, any> };

  const [formData, setFormData] = useState(defaultFormData);
  const [originalData, setOriginalData] = useState(defaultFormData);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [availableExtensions, setAvailableExtensions] = useState<Array<{ id: string; name: string; description?: string; slug?: string }>>([]);
  const [selectedMemberExtensions, setSelectedMemberExtensions] = useState<string[]>([]);
  const [originalMemberExtensions, setOriginalMemberExtensions] = useState<string[]>([]);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [initialGroups, setInitialGroups] = useState<Array<{ id: number; code: string; name: string; uuid?: string }>>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [quickListGroups, setQuickListGroups] = useState<Array<{ id: number; code: string; name: string; uuid?: string }>>([]);
  const [isQuickSearching, setIsQuickSearching] = useState(false);
  const [mohSources, setMohSources] = useState<Array<{ id: number; name: string; slug?: string }>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadMohSources = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/api/telephony/moh-sources?limit=100'), { headers: getApiHeaders() });
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) setMohSources(result.data);
      }
    } catch { /* ignore */ }
  }, []);

  const loadExtensions = useCallback(async () => {
    try {
      const response = await fetch(buildApiUrl('/api/telephony/extensions?limit=100&sortBy=extension&sortOrder=asc'), { headers: getApiHeaders() });
      if (!response.ok) throw new Error('Failed to load extensions');
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setAvailableExtensions(result.data.map((ext: { extension: string; name?: string | null }) => ({
          id: ext.extension, name: `${ext.extension}${ext.name ? ` - ${ext.name}` : ''}`,
          description: ext.name || '', slug: ext.extension
        })));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadExtensions(); loadMohSources(); }, [loadExtensions, loadMohSources]);

  useEffect(() => {
    if (!isEditMode && !duplicateId && selectedDomain?.id) {
      const loadSystemDefaults = async () => {
        try {
          const response = await fetch(buildApiUrl(`/api/telephony/system-general/${selectedDomain.id}`), { method: 'GET', headers: getApiHeaders() });
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              const defaults = result.data;
              setFormData(prev => ({ ...prev, settings: { ...prev.settings, codec: defaults.inboundCodecPrefs || prev.settings.codec || 'PCMU', holdMusic: defaults.holdMusic || prev.settings.holdMusic || 'local_stream://default' } }));
            }
          }
        } catch { /* ignore */ }
      };
      loadSystemDefaults();
    }
  }, [isEditMode, duplicateId, selectedDomain?.id]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapGroupToFormData = (group: Record<string, any>, duplicate = false) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settingsFromGroup = (group.settings as Record<string, any>) || {};
    const pinCode = settingsFromGroup.pinCode || '';
    const conferenceProfile = settingsFromGroup.conferenceProfile ?? group.conferenceProfile ?? null;
    const mergedSettings = {
      codec: group.codec || settingsFromGroup.codec || 'PCMU',
      volume: group.volume ?? settingsFromGroup.volume ?? 5,
      timeout: group.timeout ?? settingsFromGroup.timeout ?? 15,
      recordEnabled: group.recordEnabled ?? settingsFromGroup.recordEnabled ?? false,
      skipBusy: group.skipBusy ?? settingsFromGroup.skipBusy ?? false,
      pinCode, requirePin: pinCode?.trim() !== '' ? true : false,
      multicastMode: settingsFromGroup.multicastMode || 'speak',
      conferenceProfile: conferenceProfile === null ? 'page' : conferenceProfile,
      holdMusic: settingsFromGroup.holdMusic || 'local_stream://default',
      ...(group.announcementPath || settingsFromGroup.announcementPath ? { announcementPath: group.announcementPath || settingsFromGroup.announcementPath } : {}),
      ...(group.multicastAddress || settingsFromGroup.multicastAddress ? { multicastAddress: group.multicastAddress || settingsFromGroup.multicastAddress || '' } : {}),
      ...settingsFromGroup
    };
    const loaded = { code: group.code || '', name: group.name || '', context: group.context || '', mode: group.mode || 'default', enabled: group.enabled ?? true, settings: mergedSettings };
    return duplicate ? { ...loaded, code: '', name: loaded.name ? `${loaded.name} ${t('common.duplicate')}`.trim() : loaded.name } : loaded;
  };

  const loadPagingGroup = useCallback(async (loadId: string, duplicate = false) => {
    setIsLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/telephony/paging-groups/${loadId}`), { headers: getApiHeaders() });
      if (!response.ok) throw new Error();
      const result = await response.json();
      const group = result.data;
      const finalData = mapGroupToFormData(group, duplicate);
      setFormData(finalData); setOriginalData(finalData);
      if (group.members && Array.isArray(group.members)) {
        const memberExtensions = group.members.map((m: { extension: string }) => m.extension);
        setSelectedMemberExtensions(memberExtensions); setOriginalMemberExtensions(memberExtensions);
      }
      // Migrate multicastAddress to multicast_routes if needed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const s = finalData.settings as Record<string, any>;
      if (s.multicastAddress && !s.multicast_routes) {
        s.multicast_routes = [{ address: s.multicastAddress, port: 5000, mode: s.multicastMode || 'speak', interface: 'eth0', ttl: 1, codec: s.codec || 'PCMU', enabled: true }];
        delete s.multicastAddress;
      }
      if (!s.multicast_routes || !Array.isArray(s.multicast_routes)) s.multicast_routes = [];
    } catch { showErrorRef.current(t('pagingGroups.messages.loadError')); throw new Error(); }
    finally { setIsLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'pagingGroups:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;
    if (!loadId || loadId === 'new') {
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      const shouldReset = lastLoadedIdRef.current !== 'new' || lastLocationKeyRef.current !== locationKey ||
        (lastLoadedIdRef.current === 'new' && loadId === 'new' && resetTimestamp && resetTimestamp !== lastResetTimestamp);
      if (shouldReset) {
        lastLoadedIdRef.current = 'new'; lastLocationKeyRef.current = locationKey;
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) { sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp); sessionStorage.removeItem(savedDataKey + ':reset'); }
        } catch { /* ignore */ }
        setFormData(defaultFormData); setOriginalData(defaultFormData);
        setSelectedMemberExtensions([]); setOriginalMemberExtensions([]);
      }
      return;
    }
    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) return;
    lastLoadedIdRef.current = loadId; lastLocationKeyRef.current = locationKey;
    loadPagingGroup(loadId, Boolean(duplicateId)).catch(() => { lastLoadedIdRef.current = null; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateId, id, loadPagingGroup, location.key, location.pathname, location.search]);

  useEffect(() => { return () => { lastLoadedIdRef.current = null; }; }, []);

  const handleChange = (field: string, value: string | boolean | number | Record<string, unknown>) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'name' && value) newData.context = generateContext(String(value));
      return newData;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSettingChange = (key: string, value: string | boolean | number | null | Array<unknown>) => {
    setFormData(prev => {
      const newSettings = { ...prev.settings, [key]: value };
      if (key === 'pinCode') newSettings.requirePin = typeof value === 'string' && value.trim() !== '' ? true : false;
      return { ...prev, settings: newSettings };
    });
  };

  const getMulticastRoutes = (): MulticastRoute[] => {
    const routes = formData.settings?.multicast_routes;
    return Array.isArray(routes) ? routes : [];
  };
  const addMulticastRoute = () => handleSettingChange('multicast_routes', [...getMulticastRoutes(), { ...defaultMulticastRoute }]);
  const updateMulticastRoute = (index: number, field: keyof MulticastRoute, value: MulticastRoute[keyof MulticastRoute]) => {
    const updatedRoutes = [...getMulticastRoutes()];
    updatedRoutes[index] = { ...updatedRoutes[index], [field]: value };
    handleSettingChange('multicast_routes', updatedRoutes);
  };
  const removeMulticastRoute = (index: number) => handleSettingChange('multicast_routes', getMulticastRoutes().filter((_, i) => i !== index));

  const validateMulticastRoute = (route: MulticastRoute, index: number): string | null => {
    if (!route.address?.trim()) return t('pagingGroups.validation.multicastAddressRequired');
    if (!route.address.match(/^239\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) return t('pagingGroups.validation.multicastAddressInvalid');
    if (route.port < 1024 || route.port > 65535) return t('pagingGroups.validation.multicastPortInvalid');
    const dup = getMulticastRoutes().findIndex((r, i) => i !== index && r.address === route.address && r.port === route.port);
    if (dup !== -1) return t('pagingGroups.validation.multicastRouteDuplicate');
    return null;
  };

  const validateForm = (): { isValid: boolean, errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    if (!formData.code.trim()) newErrors.code = t('pagingGroups.validation.codeRequired');
    else if (!formData.code.match(/^\*?\d+$/)) newErrors.code = t('pagingGroups.validation.codeFormat');
    if (!formData.name.trim()) newErrors.name = t('pagingGroups.validation.nameRequired');
    const timeout = formData.settings?.timeout ?? 15;
    if (timeout < 5 || timeout > 120) newErrors.timeout = t('pagingGroups.validation.timeoutRange');
    if (formData.mode === 'multicast' || formData.mode === 'hybrid') {
      const routes = getMulticastRoutes();
      routes.forEach((route, index) => {
        const routeError = validateMulticastRoute(route, index);
        if (routeError) newErrors[`multicast_route_${index}`] = routeError;
      });
      if (routes.length === 0) newErrors.multicast_routes = t('pagingGroups.validation.multicastRoutesRequired');
    }
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const validation = validateForm();
    if (!validation.isValid) { 
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' � ');
      showError(`${t('pagingGroups.messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined; 
    }
    setIsSaving(true);
    try {
      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) { setIsSaving(false); return undefined; }
      lastSaveTimestamp.current = now;
      const generatedContext = formData.context || generateContext(formData.name);
      const url = isEditMode ? buildApiUrl(`/api/telephony/paging-groups/${id}`) : buildApiUrl('/api/telephony/paging-groups');
      const settingsToSave = { ...formData.settings };
      if (settingsToSave.conferenceProfile === 'page') settingsToSave.conferenceProfile = null;
      if (settingsToSave.multicast_routes?.length > 0) delete settingsToSave.multicastAddress;
      if (!settingsToSave.announcementPath) delete settingsToSave.announcementPath;
      const dataToSave = {
        code: formData.code, name: formData.name, context: generatedContext, mode: formData.mode, enabled: formData.enabled,
        members: selectedMemberExtensions, codec: formData.settings?.codec || 'PCMU',
        volume: formData.settings?.volume ?? 5, timeout: formData.settings?.timeout ?? 15,
        recordEnabled: formData.settings?.recordEnabled ?? false, skipBusy: formData.settings?.skipBusy ?? false,
        ...(formData.settings?.announcementPath ? { announcementPath: formData.settings.announcementPath } : {}),
        settings: settingsToSave
      };
      const response = await fetch(url, { method: isEditMode ? 'PUT' : 'POST', headers: { ...getApiHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(dataToSave) });
      if (!response.ok) throw new Error(t('pagingGroups.messages.saveError'));
      const result = await response.json();
      if (!result?.success) throw new Error(result?.message || t('pagingGroups.messages.saveError'));
      showSuccess(isEditMode ? t('pagingGroups.messages.updatedSuccess') : t('pagingGroups.messages.createdSuccess'));
      if (result.data?.id) {
        const reloadResponse = await fetch(buildApiUrl(`/api/telephony/paging-groups/${result.data.id}`), { headers: getApiHeaders() });
        if (reloadResponse.ok) {
          const reloadResult = await reloadResponse.json();
          if (reloadResult.success) {
            const group = reloadResult.data;
            const newOriginalData = mapGroupToFormData(group);
            setOriginalData(newOriginalData); setFormData(newOriginalData);
            if (group.members && Array.isArray(group.members)) {
              const memberExtensions = group.members.map((m: { extension: string }) => m.extension);
              setSelectedMemberExtensions(memberExtensions); setOriginalMemberExtensions(memberExtensions);
            }
          }
        }
      } else {
        setOriginalData({ ...formData, context: generatedContext });
      }
      return result.data || { ...dataToSave, id: result.data?.id };
    } catch (error) {
      showError(error instanceof Error ? error.message : t('pagingGroups.messages.saveError')); throw error;
    } finally { setIsSaving(false); }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deletePagingGroupById(id as string);
      showSuccess(t('pagingGroups.messages.deletedSuccess'));
      navigate('/pbx/applications/paging');
    } catch (error) {
      showError(error instanceof Error ? error.message : t('pagingGroups.messages.deleteError'));
      setShowDeleteModal(false);
    } finally { setIsDeleting(false); }
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData) ||
      JSON.stringify([...selectedMemberExtensions].sort()) !== JSON.stringify([...originalMemberExtensions].sort());
  }, [formData, originalData, selectedMemberExtensions, originalMemberExtensions]);

  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    if (!savedData && isEditMode && id) return { id, ...formData };
    return savedData;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, formData, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal, listPath: '/pbx/applications/paging', newPath: '/pbx/applications/paging/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/applications/paging/${savedData.uuid || savedData.id}`,
    getFormData: () => formData, storageKey: 'pagingGroups:saveAndNewData',
  });

  const handleCancel = () => {
    if (isEditMode) { setFormData({ ...originalData }); setSelectedMemberExtensions([...originalMemberExtensions]); setErrors({}); }
    else { setFormData(defaultFormData); setSelectedMemberExtensions([]); setOriginalMemberExtensions([]); setErrors({}); }
  };

  const loadInitialGroups = useCallback(async () => {
    if (initialGroups.length > 0) return;
    setIsLoadingInitial(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '100', sortBy: 'code', sortOrder: 'asc' });
      const response = await fetch(buildApiUrl(`/api/telephony/paging-groups?${params.toString()}`), { headers: getApiHeaders() });
      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) { setInitialGroups(result.data); setQuickListGroups(result.data); }
      }
    } catch (error) { console.error('Error loading initial groups:', error); }
    finally { setIsLoadingInitial(false); }
  }, [initialGroups.length]);

  const loadQuickList = useCallback(async (search: string) => {
    const normalized = search.trim();
    if (!normalized || normalized.length < 2) { setQuickListGroups(initialGroups); return; }
    setIsQuickSearching(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '50', sortBy: 'code', sortOrder: 'asc', search: normalized });
      const response = await fetch(buildApiUrl(`/api/telephony/paging-groups?${params.toString()}`), { headers: getApiHeaders() });
      if (response.ok) {
        const result = await response.json();
        if (result?.success && Array.isArray(result.data)) setQuickListGroups(result.data);
      }
    } catch (error) { console.error('Error loading quick list:', error); }
    finally { setIsQuickSearching(false); }
  }, [initialGroups]);

  useEffect(() => {
    if (showQuickList && initialGroups.length === 0) loadInitialGroups();
  }, [showQuickList, initialGroups.length, loadInitialGroups]);

  useEffect(() => {
    if (!showQuickList) return;
    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) loadQuickList(quickListSearch);
      else setQuickListGroups(initialGroups);
    }, 800);
    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialGroups]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Suppress unused variable warning for hasChanges (used in future iterations)
  void hasChanges;

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Megaphone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('pagingGroups.editTitle')} - ${formData.name || ''}` : t('pagingGroups.createTitle')}
        backButtonText={t('pagingGroups.actions.list')}
        onBack={() => navigate('/pbx/applications/paging')}
        actions={[
          { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('pagingGroups.actions.quickList'), onClick: () => setShowQuickList(true) },
          ...(isEditMode ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('pagingGroups.actions.duplicate'), onClick: () => navigateToPagingGroupDuplicate(navigate, id!) }] : []),
          { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('pagingGroups.actions.add'), onClick: () => navigate('/pbx/applications/paging/new') }
        ]}
      />

      <div className="mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 mb-6">
            <TabsTrigger value="basic">{t('pagingGroups.tabs.basic')}</TabsTrigger>
            <TabsTrigger value="advanced">{t('pagingGroups.tabs.advanced')}</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            <BasicTab formData={formData} errors={errors} availableExtensions={availableExtensions}
              selectedMemberExtensions={selectedMemberExtensions} handleChange={handleChange}
              handleSettingChange={handleSettingChange} getMulticastRoutes={getMulticastRoutes}
              addMulticastRoute={addMulticastRoute} updateMulticastRoute={updateMulticastRoute}
              removeMulticastRoute={removeMulticastRoute} onOpenMemberModal={() => setShowMemberModal(true)}
              onSubmit={handleSubmit} />
          </TabsContent>

          <TabsContent value="advanced">
            <AdvancedTab formData={formData} mohSources={mohSources} handleSettingChange={handleSettingChange} onSubmit={handleSubmit} />
          </TabsContent>
        </Tabs>
      </div>

      <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode} isSaving={isSaving} deleteDisabled={isDeleting}
        cancelText={t('common.cancel')} deleteText={t('common.delete')}
        useSaveDropdown={true} saveDropdownStorageKey="pagingGroups:saveAction" saveDisabled={isSaving} />

      <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm} title={t('pagingGroups.deleteTitle')}
        message={t('pagingGroups.messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting} />

      <MultiSelectModal isOpen={showMemberModal} onClose={() => setShowMemberModal(false)}
        title={t('pagingGroups.fields.members.label')} description={t('pagingGroups.fields.members.description')}
        items={availableExtensions} selectedIds={selectedMemberExtensions}
        onSelectionChange={(selectedIds) => setSelectedMemberExtensions(selectedIds)}
        searchPlaceholder={t('pagingGroups.fields.members.searchPlaceholder')} icon={Phone} />

      <QuickSearchModal isOpen={showQuickList} onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
        title={t('pagingGroups.title')} items={quickListGroups}
        isLoadingInitial={isLoadingInitial} isSearching={isQuickSearching}
        searchValue={quickListSearch} onSearchChange={setQuickListSearch}
        onItemSelect={(group) => { setShowQuickList(false); setQuickListSearch(''); navigate(`/pbx/applications/paging/${group.uuid || group.id}`); }}
        currentItemId={id}
        renderItem={(group, isCurrent) => (
          <button onClick={() => { if (!isCurrent) { setShowQuickList(false); setQuickListSearch(''); navigate(`/pbx/applications/paging/${group.uuid || group.id}`); } }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}>
            <div className="flex items-center gap-3">
              <Megaphone className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">{group.code}</span>
                  {isCurrent && <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">{t('pagingGroups.current', 'Current')}</span>}
                </div>
                {group.name && <div className="text-sm text-muted-foreground truncate">{group.name}</div>}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')} minCharsMessage={t('pagingGroups.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('pagingGroups.quickListEmpty', 'Start typing to search paging groups')}
        noResultsMessage={t('pagingGroups.quickListNoResults', 'No paging groups found')}
        headerIcon={<Megaphone className="h-5 w-5" />} minSearchChars={2} />
    </div>
  );
}
